import { Alert, Box, LinearProgress, Typography } from "@mui/material";
import { exchangeQuoteMessageKey } from "./options";
import { formatMoney } from "../formatters";
import type { ClaimTranslator, ExchangeQuote } from "../types";

type ExchangeQuoteSummaryProps = {
  quote: ExchangeQuote | null;
  error: string | null;
  status: string;
  t: ClaimTranslator;
};

const sumLines = (
  quote: ExchangeQuote,
  key: "return_amount" | "exchange_amount",
) => (quote.lines ?? []).reduce((total, line) => total + Number(line[key]), 0);

type ExchangeBreakdownRowProps = {
  labelKey: string;
  value: string;
  emphasized?: boolean;
  t: ClaimTranslator;
};

const ExchangeBreakdownRow = ({
  labelKey,
  value,
  emphasized,
  t,
}: ExchangeBreakdownRowProps) => (
  <Box
    display="flex"
    justifyContent="space-between"
    fontWeight={emphasized ? "bold" : undefined}
  >
    <Typography fontSize="14px" fontWeight="inherit">
      {t(`review.exchangeQuote.${labelKey}`)}
    </Typography>
    <Typography fontSize="14px" fontWeight="inherit">
      {value}
    </Typography>
  </Box>
);

const ExchangeBreakdown = ({
  quote,
  t,
}: {
  quote: ExchangeQuote;
  t: ClaimTranslator;
}) => {
  const money = (amount: number) =>
    formatMoney(amount, quote.currency || undefined) ?? "";
  const net = Number(quote.net_difference);
  const rows = [
    { labelKey: "replacement", value: money(sumLines(quote, "exchange_amount")) },
    {
      labelKey: "credit",
      value: `−${money(sumLines(quote, "return_amount"))}`,
    },
    {
      labelKey: "shipping",
      value: money(Number(quote.return_shipping_fee)),
    },
    {
      labelKey: net >= 0 ? "totalDue" : "totalCredit",
      value: money(Math.abs(net)),
      emphasized: true,
    },
  ];

  return (
    <Box mb="12px">
      {rows.map(({ labelKey, value, emphasized }) => (
        <ExchangeBreakdownRow
          key={labelKey}
          labelKey={labelKey}
          value={value}
          emphasized={emphasized}
          t={t}
        />
      ))}
    </Box>
  );
};

export const ExchangeQuoteSummary = ({
  quote,
  error,
  status,
  t,
}: ExchangeQuoteSummaryProps) => {
  if (status === "idle") return null;
  const hasShippingFee = Number(quote?.return_shipping_fee) > 0;

  return (
    <Box mb="24px">
      <Typography fontWeight="bold" fontSize="16px" mb="8px">
        {t(
          hasShippingFee
            ? "review.exchangeQuote.summaryTitle"
            : "review.exchangeQuote.title",
        )}
      </Typography>
      {status === "loading" && <LinearProgress />}
      {status === "error" && <Alert severity="error">{error}</Alert>}
      {quote && !quote.allowed && (
        <Alert severity="warning">{quote.reason}</Alert>
      )}
      {quote?.allowed && hasShippingFee && (
        <ExchangeBreakdown quote={quote} t={t} />
      )}
      {quote?.allowed && (
        <Alert severity={Number(quote.net_difference) > 0 ? "info" : "success"}>
          {t(exchangeQuoteMessageKey(quote) ?? "review.exchangeQuote.even", {
            amount:
              formatMoney(
                Math.abs(Number(quote.net_difference)),
                quote.currency || undefined,
              ) ?? "",
          })}
        </Alert>
      )}
    </Box>
  );
};
